import type { PayloadAction } from '@reduxjs/toolkit'
import {
  createSlice,
} from '@reduxjs/toolkit'
import type { RootState } from '../../app/store'
import {
  CheckinEntryUpdate,
  CheckinEvent,
  S3CheckinEntry
} from '../../app/types'
import { createLogger } from '../../app/logger'

// eslint-disable-next-line no-unused-vars
const logger = createLogger({
  prefix: 'DataSlice'
})

export type DataError = {
  message: string
}

export type DataState = {
  addOrUpdateEntries: S3CheckinEntry[]
  entriesUrl?: string
  entriesUrlFetchInProgress: boolean
  event?: CheckinEvent
  s3allTeamEntries: S3CheckinEntry[]
  s3Entries: S3CheckinEntry[]
  s3EntriesUpdatedForOfflineUse: boolean
  s3FetchInProgress: boolean
  s3TeamEntries: S3CheckinEntry[]
  updatesOurs: (CheckinEntryUpdate & { synced: boolean })[]
  updatesTheirs: CheckinEntryUpdate[]
  downloadPercentage: number
}

const initialState: DataState = {
  addOrUpdateEntries: [],
  entriesUrlFetchInProgress: false,
  s3allTeamEntries: [],
  s3Entries: [],
  s3EntriesUpdatedForOfflineUse: false,
  s3FetchInProgress: false,
  s3TeamEntries: [],
  updatesOurs: [],
  updatesTheirs: [],
  downloadPercentage: 0
}

export const dataSlice = createSlice({
  name: 'data',
  initialState,
  reducers: {
    addTheirUpdates: (state, action: PayloadAction<CheckinEntryUpdate[]>) => {
      for (const update of action.payload) {
        const existing = state.updatesTheirs.find(x => x.entryId === update.entryId)
        if (existing) {
          existing.regChoiceName = update.regChoiceName
          existing.bib = update.bib
          existing.checkedIn = update.checkedIn
          existing.checkInTime = update.checkInTime
          existing.lastModifiedTime = update.lastModifiedTime
        }
        else {
          state.updatesTheirs.push(update)
        }
      }
    },
    addUnsyncedRequests: (state, action: PayloadAction<CheckinEntryUpdate[]>) => {
      for (const update of action.payload) {
        const existing = state.updatesOurs.find(x => x.entryId === update.entryId)
        if (existing) {
          existing.regChoiceName = update.regChoiceName
          existing.raceId = update.raceId
          existing.bib = update.bib
          existing.tag = update.tag
          existing.teamId = update.teamId
          existing.teamType = update.teamType
          existing.teamShareBib = update.teamShareBib
          existing.checkedIn = update.checkedIn
          existing.checkInTime = update.checkInTime
          existing.lastModifiedTime = update.lastModifiedTime
          existing.chipDisambiguationPolicy = update.chipDisambiguationPolicy
          existing.synced = false
        }
        else {
          state.updatesOurs.push({
            ...update,
            synced: false,
          })
        }
      }
    },
    addKioskChanges: (state, action: PayloadAction<CheckinEntryUpdate[]>) => {
      for (const update of action.payload) {
        const existing = state.updatesOurs.find(x => x.entryId === update.entryId)
        if (existing) {
          existing.regChoiceName = update.regChoiceName
          existing.raceId = update.raceId
          existing.bib = update.bib
          existing.tag = update.tag
          existing.teamId = update.teamId
          existing.teamType = update.teamType
          existing.teamShareBib = update.teamShareBib
          existing.checkedIn = update.checkedIn
          existing.checkInTime = update.checkInTime
          existing.lastModifiedTime = update.lastModifiedTime
          existing.chipDisambiguationPolicy = update.chipDisambiguationPolicy
          existing.synced = update.synced || false
        }
        else {
          state.updatesOurs.push({ ...update, synced: update.synced || false })
        }
      }
    },
    markRequestsSynced: (state, action: PayloadAction<number[]>) => {
      for (const update of state.updatesOurs
        .filter(x => action.payload.includes(x.entryId))
      ) {
        update.synced = true
      }
    },
    setAddOrUpdateEntries: (state, action: PayloadAction<S3CheckinEntry[]>) => {
      state.addOrUpdateEntries = action.payload
    },
    setEntriesUrl: (state, action: PayloadAction<string>) => {
      state.entriesUrl = action.payload
      state.entriesUrlFetchInProgress = true
    },
    setEntriesUrlFetchInProgress: (state, action: PayloadAction<boolean>) => {
      state.entriesUrlFetchInProgress = action.payload
    },
    setEvent: (state, action: PayloadAction<CheckinEvent | undefined>) => {
      state.event = action.payload
    },
    setS3EntriesUpdatedForOfflineUse: (state, action: PayloadAction<boolean>) => {
      state.s3EntriesUpdatedForOfflineUse = action.payload
    },
    setS3FetchInProgress: (state, action: PayloadAction<boolean>) => {
      state.s3FetchInProgress = action.payload
    },
    updateS3Entries: (state, action: PayloadAction<S3CheckinEntry[]>) => {
      state.s3Entries = action.payload
      state.addOrUpdateEntries = []
    },
    updateS3AllTeamEntries: (state, action: PayloadAction<S3CheckinEntry[]>) => {
      state.s3allTeamEntries = action.payload
    },
    updateS3TeamEntries: (state, action: PayloadAction<S3CheckinEntry[]>) => {
      state.s3TeamEntries = action.payload
    },
    setDownloadPercentage: (state, action: PayloadAction<number>) => {
      state.downloadPercentage = action.payload
    },
  }
})

export const {
  addTheirUpdates,
  addUnsyncedRequests,
  addKioskChanges,
  markRequestsSynced,
  setAddOrUpdateEntries,
  setEntriesUrl,
  setEntriesUrlFetchInProgress,
  setEvent,
  setS3EntriesUpdatedForOfflineUse,
  setS3FetchInProgress,
  updateS3AllTeamEntries,
  updateS3Entries,
  updateS3TeamEntries,
  setDownloadPercentage,
} = dataSlice.actions

export const getAddOrUpdateEntries = (state: RootState) => state.data.addOrUpdateEntries
export const getEntriesUrlFetchInProgressSelector = (state: RootState) => state.data.entriesUrlFetchInProgress
export const getEntriesUrlSelector = (state: RootState) => state.data.entriesUrl
export const getEventSelector = (state: RootState) => state.data.event
export const getS3AllTeamEntriesSelector = (state: RootState) => state.data.s3allTeamEntries
export const getS3EntriesSelector = (state: RootState) => state.data.s3Entries
export const getS3EntriesUpdatedForOfflineUse = (state: RootState) => state.data.s3EntriesUpdatedForOfflineUse
export const getS3FetchInProgress = (state: RootState) => state.data.s3FetchInProgress
export const getS3TeamEntriesSelector = (state: RootState) => state.data.s3TeamEntries
export const getUpdatesOursSelector = (state: RootState) => state.data.updatesOurs
export const getUpdatesTheirsSelector = (state: RootState) => state.data.updatesTheirs
export const getDownloadPercentageSelector = (state: RootState) => state.data.downloadPercentage

export default dataSlice.reducer
